// Insurance Consultation Card Form — question schema.
// Each section renders a series of "cards" (one question per screen).
// Field types: text | textarea | date | number | height | select | multiselect | radio | checkboxOnly
// `condition(answers)` returns true if the question should be shown.

export const SECTIONS = [
  {
    id: "about",
    title: "About You",
    questions: [
      {
        id: "dateOfBirth",
        type: "date",
        required: true,
        title: "What is your date of birth?",
        max: "today",
        minAge: 18,
      },
      {
        id: "maritalStatus",
        type: "radio",
        required: true,
        title: "What is your marital status?",
        options: [
          "Single", "Married", "Domestic Partnership", "Divorced",
          "Separated", "Widowed", "Prefer Not to Answer",
        ],
      },
      {
        id: "height",
        type: "height",
        required: true,
        title: "What is your height?",
        fields: ["heightFeet", "heightInches"],
      },
      {
        id: "weightPounds",
        type: "number",
        required: true,
        title: "What is your approximate weight?",
        suffix: "pounds",
        min: 40,
        max: 900,
      },
      {
        id: "occupation",
        type: "text",
        required: true,
        title: "What is your occupation?",
        maxLength: 120,
      },
      {
        id: "employmentStatus",
        type: "radio",
        required: true,
        title: "What is your current employment status?",
        options: [
          "Employed Full-Time", "Employed Part-Time", "Self-Employed",
          "Business Owner", "Active-Duty Military", "Retired",
          "Homemaker or Caregiver", "Student", "Unable to Work",
          "Currently Unemployed", "Other",
        ],
        followUp: {
          when: (a) => a.employmentStatus === "Other",
          field: "employmentStatusOther",
          placeholder: "Please specify",
        },
      },
      {
        id: "annualIncome",
        type: "radio",
        required: true,
        title: "What is your approximate annual household income?",
        supportingText:
          "This information helps us understand the amount of coverage that may be appropriate. It does not guarantee eligibility.",
        options: [
          "Under $25,000", "$25,000–$49,999", "$50,000–$74,999",
          "$75,000–$99,999", "$100,000–$149,999", "$150,000–$249,999",
          "$250,000 or More", "Prefer to Discuss Privately",
        ],
      },
      {
        id: "netWorth",
        type: "radio",
        required: true,
        title: "What is your estimated net worth?",
        supportingText:
          "Consider the approximate value of savings, investments, retirement accounts, real estate equity, and business ownership, minus major debts.",
        options: [
          "Less Than $25,000", "$25,000–$99,999", "$100,000–$249,999",
          "$250,000–$499,999", "$500,000–$999,999",
          "$1 Million–$2.49 Million", "$2.5 Million or More",
          "Unsure", "Prefer to Discuss Privately",
        ],
      },
    ],
  },

  {
    id: "tobacco",
    title: "Tobacco, Nicotine & Cannabis",
    questions: [
      {
        id: "tobaccoUse",
        type: "multiselect",
        required: true,
        title: "Have you used any tobacco, nicotine, vaping, or cannabis products within the past 12 months?",
        exclusiveOption: "No",
        options: [
          "No", "Cigarettes", "Cigars", "Pipe Tobacco",
          "Chewing Tobacco or Snuff", "Nicotine Vape or E-Cigarette",
          "Nicotine Pouches", "Nicotine Gum or Patch",
          "Cannabis or Marijuana", "More Than One Product",
        ],
      },
      {
        id: "tobaccoFrequency",
        type: "radio",
        required: true,
        title: "How frequently do you use these products?",
        condition: (a) =>
          Array.isArray(a.tobaccoUse) &&
          a.tobaccoUse.length > 0 &&
          !(a.tobaccoUse.length === 1 && a.tobaccoUse[0] === "No"),
        options: [
          "Daily", "Several Times Per Week", "Several Times Per Month",
          "Occasionally", "I Recently Stopped",
        ],
      },
      {
        id: "tobaccoLastUsed",
        type: "date",
        required: true,
        title: "When did you last use the product?",
        placeholder: "Approximate date",
        condition: (a) =>
          Array.isArray(a.tobaccoUse) &&
          a.tobaccoUse.length > 0 &&
          !(a.tobaccoUse.length === 1 && a.tobaccoUse[0] === "No"),
      },
    ],
  },

  {
    id: "goals",
    title: "Your Insurance Goals",
    questions: [
      {
        id: "insuranceGoals",
        type: "multiselect",
        required: true,
        title: "What are you hoping your insurance plan will help accomplish?",
        options: [
          "Protect My Family", "Replace My Income", "Pay Off My Mortgage",
          "Cover Final Expenses", "Leave an Inheritance",
          "Build Long-Term Cash Value", "Plan for Retirement",
          "Protect My Business", "Key-Person Coverage",
          "Buy-Sell Agreement Funding", "Replace Existing Coverage",
          "Review an Existing Policy", "Supplement Employer Coverage",
          "Critical Illness Protection", "Accident Protection",
          "I Am Not Sure and Need Guidance", "Other",
        ],
        followUp: {
          when: (a) => Array.isArray(a.insuranceGoals) && a.insuranceGoals.includes("Other"),
          field: "insuranceGoalsOther",
          placeholder: "Please specify",
        },
      },
    ],
  },

  {
    id: "currentCoverage",
    title: "Current Coverage",
    questions: [
      {
        id: "currentCoverage",
        type: "radio",
        required: true,
        title: "Do you currently have life insurance?",
        options: [
          "No", "Yes, Through My Employer", "Yes, an Individual Policy",
          "Yes, Both Employer and Individual Coverage", "Unsure",
        ],
      },
      {
        id: "currentCompany",
        type: "text",
        title: "What is the name of your current insurance company?",
        placeholder: "You may enter 'Unsure'",
        condition: (a) => (a.currentCoverage || "").startsWith("Yes"),
      },
      {
        id: "currentCoverageAmount",
        type: "text",
        title: "Approximately how much coverage do you currently have?",
        placeholder: "e.g. $250,000",
        condition: (a) => (a.currentCoverage || "").startsWith("Yes"),
      },
      {
        id: "currentPremium",
        type: "text",
        title: "Approximately how much is your monthly premium?",
        placeholder: "You may enter 'Unsure'",
        condition: (a) => (a.currentCoverage || "").startsWith("Yes"),
      },
      {
        id: "reviewExistingPolicy",
        type: "radio",
        title: "Would you like us to review your current policy?",
        supportingText:
          "We will not ask you to cancel or replace an existing policy.",
        options: ["Yes", "No", "I Am Not Sure"],
        condition: (a) => (a.currentCoverage || "").startsWith("Yes"),
      },
    ],
  },

  {
    id: "family",
    title: "Family & Dependents",
    questions: [
      {
        id: "hasDependents",
        type: "radio",
        required: true,
        title: "Does anyone currently depend on your income?",
        options: ["Yes", "No", "Not Currently, but That May Change Soon"],
      },
      {
        id: "dependentTypes",
        type: "multiselect",
        required: true,
        title: "Who depends on your income?",
        condition: (a) => a.hasDependents === "Yes",
        options: [
          "Spouse or Partner", "Children", "Parents",
          "Other Family Members", "Business Partner",
          "Employees", "Other",
        ],
        followUp: {
          when: (a) => Array.isArray(a.dependentTypes) && a.dependentTypes.includes("Other"),
          field: "dependentTypesOther",
          placeholder: "Please specify",
        },
      },
      {
        id: "numberOfDependents",
        type: "radio",
        required: true,
        title: "How many financial dependents do you have?",
        condition: (a) => a.hasDependents === "Yes",
        options: ["None", "1", "2", "3", "4", "5 or More"],
      },
    ],
  },

  {
    id: "desired",
    title: "Desired Coverage",
    questions: [
      {
        id: "desiredCoverage",
        type: "radio",
        required: true,
        title: "How much life insurance would you like to explore?",
        options: [
          "Less Than $50,000", "$50,000", "$100,000", "$250,000",
          "$500,000", "$750,000", "$1 Million", "More Than $1 Million",
          "I Am Not Sure — Please Help Me Calculate It",
        ],
      },
      {
        id: "monthlyBudget",
        type: "radio",
        required: true,
        title: "What monthly budget would feel comfortable?",
        supportingText:
          "This helps us avoid recommending options that do not fit your budget.",
        options: [
          "Under $50 per Month", "$50–$99 per Month", "$100–$149 per Month",
          "$150–$249 per Month", "$250–$399 per Month", "$400 or More per Month",
          "Flexible", "I Am Not Sure",
        ],
      },
    ],
  },

  {
    id: "health",
    title: "General Health",
    intro:
      "The following general health questions help us understand which insurance options may be appropriate. This is not a formal insurance application. Please do not enter Social Security numbers, medical record numbers, or account information.",
    questions: [
      {
        id: "healthConditions",
        type: "multiselect",
        required: true,
        title:
          "Within the past five years, have you been diagnosed with, treated for, or prescribed medication for any of the following?",
        exclusiveOption: "None of the Above",
        options: [
          "Heart Disease, Heart Attack, or Chest Pain", "High Blood Pressure",
          "High Cholesterol", "Stroke or Transient Ischemic Attack",
          "Diabetes", "Cancer or Tumors", "Kidney Disease", "Liver Disease",
          "COPD or Emphysema", "Asthma", "Sleep Apnea", "Depression",
          "Anxiety", "PTSD", "Other Mental-Health Condition",
          "Neurological Condition", "Seizures", "Autoimmune Disorder",
          "Recent Surgery", "Recent Hospitalization",
          "Alcohol or Substance-Use Treatment", "Other",
          "None of the Above", "Prefer to Discuss Privately",
        ],
      },
      {
        id: "healthConditionsDetail",
        type: "textarea",
        title:
          "Please briefly identify the condition and approximate date of diagnosis or treatment.",
        supportingText:
          "Do not include Social Security numbers, medical record numbers, insurance account numbers, or detailed medical documents.",
        condition: (a) =>
          Array.isArray(a.healthConditions) &&
          a.healthConditions.some(
            (c) => c !== "None of the Above" && c !== "Prefer to Discuss Privately",
          ),
      },
      {
        id: "takingMedications",
        type: "radio",
        required: true,
        title: "Are you currently taking any prescription medications?",
        options: ["Yes", "No", "Prefer to Discuss Privately"],
      },
      {
        id: "medicationsDetail",
        type: "textarea",
        title:
          "Please list the medication names and the general conditions they are used to treat.",
        condition: (a) => a.takingMedications === "Yes",
      },
      {
        id: "priorInsuranceDecline",
        type: "radio",
        required: true,
        title:
          "Have you ever been declined, postponed, or charged a higher rate for life insurance?",
        options: ["Yes", "No", "Not Sure", "I Have Never Applied Before"],
      },
      {
        id: "priorInsuranceDeclineDetail",
        type: "textarea",
        title: "Please provide the approximate year and reason, if known.",
        condition: (a) => a.priorInsuranceDecline === "Yes",
      },
    ],
  },

  {
    id: "background",
    title: "Optional Background",
    questions: [
      {
        id: "militaryService",
        type: "radio",
        required: true,
        title: "Have you served in the United States Armed Forces?",
        options: [
          "No", "Currently Serving", "Veteran",
          "Retired From Military Service", "Prefer Not to Answer",
        ],
      },
      {
        id: "militaryBranch",
        type: "radio",
        title: "Which branch did you serve in?",
        condition: (a) =>
          ["Currently Serving", "Veteran", "Retired From Military Service"].includes(a.militaryService),
        options: [
          "Army", "Marine Corps", "Navy", "Air Force", "Space Force",
          "Coast Guard", "National Guard", "Reserves",
          "More Than One", "Other",
        ],
      },
      {
        id: "businessOwner",
        type: "radio",
        required: true,
        title: "Are you a business owner or self-employed?",
        options: ["Yes", "No", "Planning to Start a Business"],
      },
      {
        id: "businessProtection",
        type: "multiselect",
        title: "Which business-protection topics interest you?",
        condition: (a) =>
          a.businessOwner === "Yes" || a.businessOwner === "Planning to Start a Business",
        options: [
          "Key-Person Coverage", "Buy-Sell Agreement Funding",
          "Business Loan Protection", "Executive Benefits",
          "Employee Benefits", "Business-Succession Planning",
          "Personal Life Insurance", "Other", "I Am Not Sure",
        ],
      },
    ],
  },

  {
    id: "contact",
    title: "Contact Information",
    questions: [
      {
        id: "name",
        type: "nameGroup",
        required: true,
        title: "Full legal name",
        fields: ["firstName", "middleName", "lastName", "suffix"],
      },
      {
        id: "zipCode",
        type: "text",
        required: true,
        title: "ZIP Code",
        pattern: "^\\d{5}$",
        placeholder: "5-digit ZIP",
        maxLength: 5,
      },
      {
        id: "email",
        type: "text",
        required: true,
        inputType: "email",
        title: "Email Address",
        placeholder: "you@example.com",
      },
      {
        id: "phone",
        type: "text",
        required: true,
        title: "Phone Number",
        placeholder: "(555) 123-4567",
        pattern: "^[\\d\\s().+-]{10,20}$",
      },
      {
        id: "preferredContact",
        type: "radio",
        required: true,
        title: "How would you prefer that we contact you?",
        options: ["Phone Call", "Text Message", "Email", "Video Meeting"],
      },
      {
        id: "bestTimeToContact",
        type: "radio",
        required: true,
        title: "What is the best time to reach you?",
        options: ["Morning", "Afternoon", "Evening", "Flexible"],
      },
      {
        id: "preferredLanguage",
        type: "radio",
        required: true,
        title: "What is your preferred language?",
        options: ["English", "Spanish", "Other"],
        followUp: {
          when: (a) => a.preferredLanguage === "Other",
          field: "preferredLanguageOther",
          placeholder: "Please specify",
        },
      },
      {
        id: "hearAboutUs",
        type: "radio",
        required: true,
        title: "How did you hear about Noble Insurance?",
        options: [
          "Google", "Facebook", "Instagram", "YouTube",
          "Noble Strategies Consulting Website", "Existing Noble Client",
          "Friend or Family", "Insurance Advisor Referral",
          "Veteran Organization", "Community Event", "Other",
        ],
        followUp: {
          when: (a) => a.hearAboutUs === "Insurance Advisor Referral",
          field: "referrerName",
          placeholder: "Who referred you?",
        },
        followUpAlt: {
          when: (a) => a.hearAboutUs === "Other",
          field: "hearAboutUsOther",
          placeholder: "Please tell us how you heard about Noble Insurance",
        },
      },
      {
        id: "referredByAdvisor",
        type: "radio",
        required: true,
        title: "Were you referred by a Noble Insurance advisor?",
        options: ["Yes", "No"],
        followUp: {
          when: (a) => a.referredByAdvisor === "Yes",
          field: "advisorName",
          placeholder: "Advisor's name",
        },
      },
      {
        id: "finalComments",
        type: "textarea",
        title: "Is there anything else you would like us to know?",
        supportingText:
          "Share any questions, concerns, deadlines, or special circumstances you would like your advisor to consider.",
      },
    ],
  },
];

// Total visible questions helper (respects conditions)
export function visibleQuestions(answers) {
  const list = [];
  SECTIONS.forEach((section) => {
    section.questions.forEach((q) => {
      if (!q.condition || q.condition(answers)) {
        list.push({ ...q, section: section.id, sectionTitle: section.title });
      }
    });
  });
  return list;
}
